/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Apple, Carrot, Sparkles, Star, ChevronDown } from 'lucide-react';
import { MONTHS_SR, SEASONAL_DATA, getCurrentMonthIndex } from '../utils/seasonalProducts';
import { useIsMobile } from '../utils/useIsMobile';

export default function SeasonalCalendar({ onPickItem }) {
  const [current, setCurrent] = useState(getCurrentMonthIndex);
  const data = SEASONAL_DATA[current];
  // Na mobilnom je kalendar skupljen po defaultu da ne gura oglase ispod folda.
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const isOpen = !isMobile || expanded;

  const handlePick = (name) => {
    if (typeof onPickItem === 'function') onPickItem(name);
  };

  return (
    <div className="bg-white border border-sage-100 rounded-3xl shadow-sm p-6 sm:p-8">
      <button
        type="button"
        onClick={() => isMobile && setExpanded((o) => !o)}
        aria-expanded={isOpen}
        className={`w-full flex items-center gap-3 text-left ${isOpen ? 'mb-5' : ''} ${isMobile ? '' : 'cursor-default'}`}
      >
        <div className="w-9 h-9 rounded-2xl bg-sage-50 flex items-center justify-center text-sage-600 flex-shrink-0">
          <Sparkles size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-serif font-bold text-earth-900 leading-tight">Sezonski kalendar</h3>
          <p className="text-xs text-sage-500">
            {isOpen ? 'Šta je sezonski u Srbiji ovog meseca' : `Šta je sezonski u ${MONTHS_SR[current].full.toLowerCase()}u?`}
          </p>
        </div>
        {isMobile && (
          <ChevronDown
            size={20}
            className={`text-sage-500 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="calendar-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
      <div className="flex flex-wrap gap-1.5 mb-5">
        {MONTHS_SR.map((m, i) => (
          <button
            key={m.short}
            onClick={() => setCurrent(i)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
              i === current
                ? 'bg-sage-600 border-sage-600 text-white font-bold shadow-sm shadow-sage-600/20'
                : 'bg-sage-50 border-sage-100 text-sage-700 hover:bg-sage-100 hover:border-sage-200'
            }`}
            aria-label={`Prikaži ${m.full}`}
          >
            {m.short}
          </button>
        ))}
      </div>

      <div className="bg-sage-50/60 border-l-4 border-sage-500 rounded-r-xl px-4 py-3 mb-6">
        <p className="text-sm text-sage-800 leading-relaxed">
          <span className="font-bold text-earth-900 mr-1">{MONTHS_SR[current].full}:</span>
          {data.info}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <SeasonSection
          title="Voće"
          icon={<Apple size={14} />}
          items={data.fruits}
          peak={data.peakFruits}
          variant="fruit"
          onPick={handlePick}
          emptyText="Nema svežeg sezonskog voća ovog meseca."
        />
        <SeasonSection
          title="Povrće"
          icon={<Carrot size={14} />}
          items={data.veg}
          peak={data.peakVeg}
          variant="veg"
          onPick={handlePick}
        />
      </div>

      <div className="flex items-center gap-1.5 mt-5 pt-4 border-t border-sage-100 text-[11px] text-sage-500">
        <Star size={12} className="fill-amber-400 text-amber-500" />
        <span>Vrhunac sezone</span>
      </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SeasonSection({ title, icon, items, peak, variant, onPick, emptyText }) {
  const baseClasses =
    variant === 'fruit'
      ? 'bg-amber-50 text-amber-900 border-amber-200 hover:bg-amber-100 hover:border-amber-300'
      : 'bg-sage-50 text-sage-900 border-sage-200 hover:bg-sage-100 hover:border-sage-300';

  const peakClasses =
    variant === 'fruit'
      ? 'bg-amber-100 text-amber-950 border-amber-500 border-2 font-bold shadow-sm'
      : 'bg-sage-100 text-sage-950 border-sage-600 border-2 font-bold shadow-sm';

  // Vrhunac sezone uvek prvi, ostali zadržavaju svoj redosled
  const sortedItems = useMemo(() => {
    const peakSet = new Set(peak);
    return [...items].sort((a, b) => {
      const aPeak = peakSet.has(a) ? 0 : 1;
      const bPeak = peakSet.has(b) ? 0 : 1;
      return aPeak - bPeak;
    });
  }, [items, peak]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wide text-sage-600 font-bold">
        {icon}
        <span>{title}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-sage-400 italic">{emptyText || 'Trenutno ništa sezonski.'}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {sortedItems.map((item) => {
            const isPeak = peak.includes(item);
            return (
              <button
                key={item}
                onClick={() => onPick(item)}
                className={`inline-flex items-center gap-1 text-sm px-3 py-1 rounded-full border transition-all hover:scale-105 cursor-pointer ${
                  isPeak ? peakClasses : baseClasses
                }`}
              >
                {isPeak && (
                  <Star
                    size={12}
                    className={variant === 'fruit' ? 'fill-amber-500 text-amber-600' : 'fill-sage-600 text-sage-700'}
                  />
                )}
                {item}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
